import { createSelector } from '@ngrx/store';
import { EventsState, eventStateSelector, JoiEvent } from '../events.era';
import { AccountsState, accountStateSelector } from '../accounts.era';
import { listOfIdsToEmptySetsMap } from '../../helpers/helpers';
import { eventListReselector } from './events.reselector';
import { User } from '../../../../globals/store/user.data';
import { FilterGroup, FilterGroupTypes, filterPageTypes, FiltersState } from '../../../../globals/store/filters.data';
import { Dictionary } from '@ngrx/entity';
import { CalendarEvent, getEventCalendarDateBlocksMap } from './calendars.reselector';
import { activeDocketStatuses } from '../../../../globals/store/accounts.data';
import { FrontPageFilterUIParams } from '../../services/filters.service';
import { state } from '@angular/animations';
import { userStateSelector } from '../user.era';
import { settingsStateSelector } from '../settings.era';
import { filterStateSelector } from '../filters.era';
import { calendarStateSelector } from '../calendars.era';
import { Label } from '../../../../globals/store/events.data';
import { makeMapFromListOfObjectsAndKey, makeMapFromListOfObjectsAndKeyToProperty } from 'globals/helpers/helpers';

const eventDictionary = createSelector(eventStateSelector, (state) => state.entities)
const events = createSelector(eventStateSelector, (state) => Object.values(state.entities))
const you = createSelector(userStateSelector, (state) => state.user)
const showClosedEvents = createSelector(settingsStateSelector, (state) => state.user.event_show_closed)
const reverseEventOrder = createSelector(settingsStateSelector, (state) => state.user.event_order_rev)
const eventOrderKey = createSelector(settingsStateSelector, (state) => state.user.event_order_key)
const allFilters = createSelector(filterStateSelector, (filters) => filters)
const calendarDates = createSelector(calendarStateSelector, (state) => state.calendarDates)

export const invitedToEventsReselector = createSelector(
  events,
  (all_events: JoiEvent[]): JoiEvent[] => all_events
  .filter(e=>e.is_invited && e.invite_sender)
  .sort(({invite_sender:s1},{invite_sender:s2})=> s1 > s2 ? 1 : -1)
  .map(removeRepeatingInviteSenders)
)
const removeRepeatingInviteSenders = (event:JoiEvent, index: number, array) => index === 0 || array[index-1].invite_sender.uid  !== event.invite_sender.uid ? event : { ...event, 'invite_sender': null }



export const eventViewReselector = createSelector(
  eventStateSelector,
  accountStateSelector,
  (eventsState: EventsState, accounts: AccountsState) => {
    const eventLabelsByEventId: { [id: string]: string[] } = {}
    if (!accounts.entities || !eventsState.entities) {
      return { eventLabelsByEventId }
    }
    const accountLabelsByAccountId = makeMapFromListOfObjectsAndKeyToProperty(Object.values(accounts.entities), 'id', 'labels')

    Object.values(eventsState.entities).map((e: JoiEvent) => eventLabelsByEventId[e.eventId] = accountLabelsByAccountId[e.accountId])
    return { eventLabelsByEventId, accountLabelsByAccountId }
  }
)

// This is meant to only pull the data that builds the UI, minus the filters.  If I put filters in here, too, it would rebuild the entire UI every time a filter was applied, not a good idea.  Because of that, this just returns the data that you need, that then has to be combined with the current filters (one time) to build the UI
export const frontPageFilterUIDataReselector = createSelector(
  accountStateSelector,
  allFilters,
  eventListReselector,
  invitedToEventsReselector,
  you, (
    accountState: AccountsState,
    allFilters,
    eventsList: JoiEvent[],
    invitedToEvents: JoiEvent[],
    you: User): FrontPageFilterUIParams => {
console.log({ accountState, allFilters, eventsList, invitedToEvents, you })
      const accountIdByEventId = makeMapFromListOfObjectsAndKeyToProperty(eventsList, 'eventId', 'accountId')
      invitedToEvents.forEach(e => delete accountIdByEventId[e.eventId])
      const eventAccountIds = new Set(Object.values(accountIdByEventId))
      // const eventAccountIds = new Set(eventsList.map(e => e.accountId).filter(id => !invitedEventIds.has(id)))

      const accountList = Object.values(accountState.entities)
        .filter(account=>!account.is_invited)

      const accountIdsList = accountList.map(account => account.id)
      

      const publicInfoList = Object.values(accountState.public_info)
        .filter(({ id, docket }) => !accountIdsList.includes(id) && eventAccountIds.has(id))


      // console.log({ you, accountList, publicInfoList })
      if (!you || (!accountList.length && !publicInfoList.length) || !eventsList.length ) {
        return null // make sure to check for null when pulling this data
      }

      const accountLabelListsByAccountId = makeMapFromListOfObjectsAndKeyToProperty(accountList, 'id', 'labels')
      const usedAccountLabelSetsByAccountId = listOfIdsToEmptySetsMap<string>(accountIdsList)
      eventsList.filter(e => e.labels).forEach(({ labels, accountId }) =>
        labels.forEach(l => {
          if (!accountLabelListsByAccountId[accountId] || !accountLabelListsByAccountId[accountId].find((label: Label) => label.id === l)) {

            // If a user has not accepted an account invite, they wont see the account labels
            // console.log('Missing label for account', { label: l, accountId, account: accountState.entities[accountId]})
          } else {
            usedAccountLabelSetsByAccountId[accountId].add(`${l}|${accountId}`)
          }
        }))
      const inactiveAccountsList = accountList.filter(({ docket }) => !activeDocketStatuses.includes(docket?.status))
      const inactiveAccountIdSet = new Set(inactiveAccountsList.map(({ id }) => id))
      publicInfoList.filter(({ docket }) => !activeDocketStatuses.includes(docket?.status)).forEach(({ id }) => inactiveAccountIdSet.add(id))

      return { 
        accountList: accountList.filter(({ docket }) => activeDocketStatuses.includes(docket?.status)), 
        publicInfoList: publicInfoList.filter(({ docket }) => activeDocketStatuses.includes(docket?.status)),
        inactiveAccountsList, inactiveAccountIdSet, accountIdsList, you, usedAccountLabelSetsByAccountId, accountLabelListsByAccountId, filters: allFilters.home }
  }
)

export const accountEventLabelMapsReselector = createSelector(
  frontPageFilterUIDataReselector,
  (frontPageData) => {
    const accountEventLabelMaps: { [accountId: string]: { [labelId: string]: Label } } = {}
    if (frontPageData) {
      const { accountLabelListsByAccountId } = frontPageData
      Object.keys(accountLabelListsByAccountId).forEach(accountId => 
        accountEventLabelMaps[accountId] = makeMapFromListOfObjectsAndKey(accountLabelListsByAccountId[accountId], 'id')
      )
    }
    return accountEventLabelMaps
  }
)

const passesEventLabelFilter = (event: JoiEvent, allLabels: string[]): boolean => {
  if (!allLabels) return true
  
  const sameAccountLabels = allLabels.map(l => l.split("|")).filter(l => l[1] === event.accountId).map(l => l[0]) // the label is of the format "{eventLabel}|{accountId}"
  if (sameAccountLabels.length && !event.labels) return false

  return !sameAccountLabels.length || event.labels.some(l => sameAccountLabels.includes(l))
}

const passesSameAccountEventLabelFilter = (event: JoiEvent, allLabels: string[], accountFilters: string[]): boolean => {
  // console.log({ event, accountFilters, allLabels })
  if (!allLabels) return false
  const sameAccountLabels = allLabels.map(l => l.split("|")).filter(l => l[1] === event.accountId).map(l => l[0])
  if (sameAccountLabels.length && (!event.labels || !event.labels.length)) return false
  const passes = (sameAccountLabels.length && event.labels.some(l => sameAccountLabels.includes(l))) || (!sameAccountLabels.length && accountFilters && accountFilters.includes(event.accountId))
  return passes
}

const passesAccountFilter = (event: JoiEvent, filters: FilterGroup, accounts:AccountsState, yourUid:string): boolean =>
  (!filters.accounts && !filters.eventLabels) ||
  (filters.accounts && filters.accounts.filter(accountId=>
      event.users.includes(yourUid) ||
      (accounts.entities[accountId] && !accounts.entities[accountId].is_invited)
    ).includes(event.accountId)) || passesSameAccountEventLabelFilter(event, filters.eventLabels, filters.accounts) || 
  false

const passesSearchFilter = (event: JoiEvent, searchTerm: string): boolean => !searchTerm || event.title.toLowerCase().includes(searchTerm.toLowerCase())

const passesMyEventsOrMyWorkforceEvents = (filters: FilterGroup, yourUId: string, e: JoiEvent): boolean =>
  (!filters.myEvents && !filters.workforce) ||
  (filters.myEvents && filters.workforce && (e.owner_uid === yourUId || e.workforce.filter(w => w.uid).map(w => w.uid).includes(yourUId))) ||
  (!filters.workforce && filters.myEvents && e.owner_uid === yourUId) ||
  (!filters.myEvents && filters.workforce && e.workforce.filter(w => w.uid).map(w => w.uid).includes(yourUId)) ||
  false

const isOnActiveAccount = (event: JoiEvent, accounts: AccountsState): boolean => {
  return true;
}

export const filterHomeEvents = (filters: FilterGroup, eventList: JoiEvent[], showClosedEvents: boolean, accounts:AccountsState, you: User, noEventSearchFilter: boolean = false) => eventList.filter(e => ![
    () => isOnActiveAccount(e, accounts),
    () => showClosedEvents || !e.closed,
    () => passesMyEventsOrMyWorkforceEvents(filters, you.uid, e),
    () => passesAccountFilter(e, filters, accounts, you.uid),
    () => passesEventLabelFilter(e, filters.eventLabels),
    () => noEventSearchFilter || passesSearchFilter(e, filters.search)
  ].some(passesFilterFunction => !passesFilterFunction()))

export const sortEvents = (eventList: JoiEvent[], eventOrderKey: string, reverseEventOrder: boolean) => {
  const dir = reverseEventOrder ? -1 : 1
  return eventOrderKey ==='first_date' ?
    eventList.sort(({ first_date: d1}, {first_date: d2 }) => !d1 ? -dir : !d2 ? dir : d1 < d2 ? -dir : dir) :
    eventList.sort(({ title: t1}, {title: t2 }) => (t1.toLowerCase() < t2.toLowerCase() ? 1 : -1) * -dir)
}

export const filteredEventsReselectorCreator = (filterPageType: keyof FilterGroupTypes, noEventSearchFilter: boolean = false) => createSelector(
  frontPageFilterUIDataReselector,
  showClosedEvents,
  reverseEventOrder,
  eventOrderKey,
  allFilters,
  eventListReselector,
  accountStateSelector,
  you,
  (
    frontPageFilterUIDataReselector,
    showClosedEvents: boolean,
    reverseEventOrder: boolean,
    eventOrderKey: string,
    allFilters: FiltersState,
    eventList: JoiEvent[],
    accounts: AccountsState,
    you,
  ) => {
    if (!you || !eventList.length) {
      return []
    }
    const { inactiveAccountIdSet } = frontPageFilterUIDataReselector
    const filters = allFilters[filterPageType]
    const filteredEvents = filterHomeEvents(filters, eventList.filter(e => !inactiveAccountIdSet.has(e.accountId)), showClosedEvents, accounts, you, noEventSearchFilter)
    // console.log({ filters, filteredEvents, eventList })
    return sortEvents(filteredEvents, eventOrderKey, reverseEventOrder)
  }
)

export const filteredFrontPageEventsReselector = filteredEventsReselectorCreator(filterPageTypes.home)
export const filteredAllTasksTableEventsReselector = filteredEventsReselectorCreator(filterPageTypes.allTasks, true)
export const filteredAllTasksCalendarEventsReselector = filteredEventsReselectorCreator(filterPageTypes.allTasksCalendar, true)

const calendarEventsReselectors = {
  [filterPageTypes.home]: filteredFrontPageEventsReselector,
  [filterPageTypes.allTasks]: filteredAllTasksTableEventsReselector,
  [filterPageTypes.allTasksCalendar]: filteredAllTasksCalendarEventsReselector,
}

export const filteredCalendarEventsReselectorGenerator = (filterPageType: keyof FilterGroupTypes) => createSelector(
  calendarEventsReselectors[filterPageType],
  eventDictionary,
  you,
  (filteredEvents: JoiEvent[], events: Dictionary<JoiEvent>, you): { calendarEventsByDate: Dictionary<CalendarEvent[]>, eventIds: Set<string> } => {
    // console.log({ filteredEvents, events, you })
    if (!you || !filteredEvents?.length) return { calendarEventsByDate: {}, eventIds: new Set() } 
    const eventIds = new Set(filteredEvents.filter(e => !e.closed).map(e => e.eventId))
    return { calendarEventsByDate: getEventCalendarDateBlocksMap(events, eventIds), eventIds }
  }
)